import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Suggestion {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly score: number;
  readonly performanceRating: number | null;
  // Zero means the picker below would be empty for this vendor.
  readonly workOrderLines: number;
  // The save's own reason for refusing this vendor for this job, if any.
  readonly refusal: { readonly code: string; readonly message: string } | null;
}

interface JobSummary {
  readonly jobNumber: string | null;
  readonly customerName: string | null;
  readonly areaRequested: string | number | null;
  readonly areaUnit: string | null;
}

interface WorkOrderOption {
  readonly workOrderId: string;
  readonly workOrderLineId: string;
  readonly woNumber: string;
  readonly woIssueDate: string;
  readonly rateValue: number;
  readonly allottedQuantity: number;
  readonly consumedQuantity: number;
  readonly balanceQuantity: number;
  readonly unitOfMeasure: string;
  readonly planningCycleId: string;
  readonly brandingTypeId: string;
  readonly brandingItemId: string | null;
  readonly geoNodeId: string | null;
  readonly brandingTypeName: string | null;
  readonly brandingItemName: string | null;
}

interface WorkOrderPicker {
  readonly geoNodeId: string | null;
  readonly unitOfMeasure: string | null;
  readonly requestedQuantity: number | null;
  readonly options: readonly WorkOrderOption[];
}

export function BoAssignmentPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  // Stored quantities and rates are written exactly, grouped the tenant's way.
  const { decimal, number } = useBusinessNumber();
  const { jobRequestId } = useParams<{ jobRequestId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [items, setItems] = useState<readonly Suggestion[] | null>(null);
  const [job, setJob] = useState<JobSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [picker, setPicker] = useState<WorkOrderPicker | null>(null);
  const [workOrderLineId, setWorkOrderLineId] = useState('');
  const [allocatedQuantity, setAllocatedQuantity] = useState('');
  const [expectedStartDate, setExpectedStartDate] = useState('');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  // A job that has been with a vendor before is re-assigned, not assigned: the
  // server ends whatever is still open and records why. Known from the job's
  // own assignment history, so no status is spelt out here.
  const [reassigning, setReassigning] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    let alive = true;
    apiRequest<readonly Suggestion[]>(`/v1/vendor-assignments/suggest?jobRequestId=${jobRequestId}&topN=5`, { token })
      .then((r) => { if (alive) setItems(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, jobRequestId]);

  // A reviewer allocates a job, not an identifier, so the request is named here.
  useEffect(() => {
    let alive = true;
    apiRequest<JobSummary>(`/v1/job-requests/${jobRequestId}`, { token })
      .then((r) => { if (alive) setJob(r); })
      .catch(() => { /* the heading falls back to the id */ });
    return () => { alive = false; };
  }, [token, jobRequestId]);

  useEffect(() => {
    let alive = true;
    apiRequest<readonly unknown[]>(`/v1/vendor-assignments/history/${jobRequestId}`, { token })
      .then((r) => { if (alive) setReassigning(Array.isArray(r) && r.length > 0); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, jobRequestId]);

  // The order carries both the ceiling and the rate, so it is chosen per vendor.
  useEffect(() => {
    if (!vendorId) { setPicker(null); setWorkOrderLineId(''); return; }
    let alive = true;
    apiRequest<WorkOrderPicker>(
      `/v1/vendor-assignments/open-work-orders/by-job?jobRequestId=${jobRequestId}&vendorId=${vendorId}`,
      { token },
    )
      .then((r) => {
        if (!alive) return;
        setPicker(r);
        setWorkOrderLineId(r.options.length === 1 ? r.options[0].workOrderLineId : '');
        setAllocatedQuantity(r.requestedQuantity != null ? String(r.requestedQuantity) : '');
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, jobRequestId, vendorId]);

  const chosen = picker?.options.find((o) => o.workOrderLineId === workOrderLineId) ?? null;

  async function assign() {
    if (!vendorId) { setError('Select a vendor'); return; }
    if (!chosen) { setError('Select the work order this job is allocated against'); return; }
    if (!allocatedQuantity || Number(allocatedQuantity) <= 0) { setError('Allocated quantity required'); return; }
    // The server refuses this too, but the figure is on screen beside the
    // field, so there is no reason to make the user wait for a round trip.
    if (Number(allocatedQuantity) > chosen.balanceQuantity) {
      setError(
        `Work order ${chosen.woNumber} has only ${decimal(chosen.balanceQuantity)} ${chosen.unitOfMeasure} left ` +
        `of ${decimal(chosen.allottedQuantity)}. Reduce the quantity or pick another work order.`,
      );
      return;
    }
    // The acceptance deadline is the tenant's own setting
    // (M07_VENDOR_ACCEPTANCE_WINDOW_HRS, System Config), applied by the server.
    if (!expectedStartDate || !expectedCompletionDate) { setError('Start and completion dates required'); return; }
    if (expectedStartDate > expectedCompletionDate) { setError('Start must be ≤ completion'); return; }
    if (reassigning && (!reason.trim() || !remarks.trim())) { setError(t('bo.assign.reasonRemarksRequired')); return; }
    setError(null);
    const line = {
      expectedStartDate, expectedCompletionDate, priority,
      workOrderId: chosen.workOrderId,
      // The line, not just the order: an order can carry several lines,
      // each with its own remaining quantity and its own rate.
      workOrderLineId: chosen.workOrderLineId,
      planningCycleId: chosen.planningCycleId,
      brandingTypeId: chosen.brandingTypeId,
      brandingItemId: chosen.brandingItemId,
      geoNodeId: chosen.geoNodeId,
      unitOfMeasure: chosen.unitOfMeasure,
      allocatedQuantity: Number(allocatedQuantity),
    };
    try {
      await apiRequest(reassigning ? '/v1/vendor-assignments/reassign' : '/v1/vendor-assignments', {
        method: 'POST',
        token,
        body: reassigning
          ? { jobRequestId, newVendorId: vendorId, reason: reason.trim(), remarks: remarks.trim(), ...line }
          : { jobRequestId, vendorId, ...line },
      });
      navigate(`/bo/assignment-history/${jobRequestId}`);
    } catch (e) { setError((e as Error).message); }
  }

  if (error && (!items || reassigning === null)) return <main aria-label="bo-assignment"><p role="alert">{error}</p></main>;
  if (!items || reassigning === null) return <main aria-label="bo-assignment"><p>{t('bo.assign.loading')}</p></main>;

  return (
    <main aria-label="bo-assignment">
      <h1>{t(reassigning ? 'bo.assign.reassignTitle' : 'bo.assign.title', { id: job?.jobNumber ?? jobRequestId ?? '' })}</h1>
      {job ? (
        <p aria-label="job-summary">
          {[job.customerName, job.areaRequested != null ? `${decimal(job.areaRequested)} ${job.areaUnit ?? ''}`.trim() : null]
            .filter(Boolean)
            .join(' \u00b7 ')}
        </p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}

      {items.length === 0 ? (
        <p aria-label="empty">{t('bo.assign.empty')}</p>
      ) : (
        <table aria-label="suggest-table">
          <thead><tr><th>{t('bo.assign.col.pick')}</th><th>{t('bo.assign.col.vendor')}</th><th>{t('bo.assign.col.code')}</th><th>{t('bo.assign.col.score')}</th><th>{t('bo.assign.col.rating')}</th><th>{t('bo.assign.col.workOrders')}</th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.vendorId} aria-label={`row-${s.vendorId}`}>
                <td>
                  <input
                    type="radio"
                    name="vendor-pick"
                    aria-label={`pick-${s.vendorId}`}
                    checked={vendorId === s.vendorId}
                    disabled={s.workOrderLines === 0 || s.refusal !== null}
                    onChange={() => setVendorId(s.vendorId)}
                  />
                </td>
                <td>{s.vendorName}</td>
                <td>{s.vendorCode}</td>
                <td>{number(s.score, 2)}</td>
                <td>{s.performanceRating ?? '\u2014'}</td>
                <td aria-label={`cover-${s.vendorId}`}>
                  {s.refusal
                    ? s.refusal.message
                    : s.workOrderLines > 0
                      ? s.workOrderLines
                      : t('bo.assign.noWorkOrderInUnit')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section aria-label="work-order-section">
        <h2>Work order</h2>
        {!vendorId ? (
          <p aria-label="wo-hint">Pick a vendor to see the work orders this job can be allocated against.</p>
        ) : !picker ? (
          <p>Loading work orders…</p>
        ) : picker.options.length === 0 ? (
          <p role="alert" aria-label="wo-empty">
            This job is measured in {picker.unitOfMeasure ?? 'no unit'}, and this vendor holds no
            approved work order with balance left in that unit. Work committed in another unit —
            a TV or cinema order counted in spots, for instance — cannot be drawn on by a site job.
          </p>
        ) : (
          <table aria-label="work-order-table">
            <thead>
              <tr><th>Pick</th><th>WO number</th><th>Issued</th><th>Branding line</th><th>Rate</th><th>Allotted</th><th>Consumed</th><th>Balance</th><th>UOM</th></tr>
            </thead>
            <tbody>
              {picker.options.map((o) => (
                <tr key={o.workOrderLineId} aria-label={`wo-row-${o.workOrderLineId}`}>
                  <td>
                    <input
                      type="radio"
                      name="wo-pick"
                      aria-label={`wo-pick-${o.workOrderLineId}`}
                      checked={workOrderLineId === o.workOrderLineId}
                      onChange={() => setWorkOrderLineId(o.workOrderLineId)}
                    />
                  </td>
                  <td>{o.woNumber}</td>
                  <td>{day(o.woIssueDate)}</td>
                  <td>{[o.brandingTypeName, o.brandingItemName].filter(Boolean).join(' / ') || '—'}</td>
                  <td>{decimal(o.rateValue)}</td>
                  <td>{decimal(o.allottedQuantity)}</td>
                  <td>{decimal(o.consumedQuantity)}</td>
                  <td>{decimal(o.balanceQuantity)}</td>
                  <td>{o.unitOfMeasure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <label htmlFor="qty-input">Allocated quantity{chosen ? ` (${chosen.unitOfMeasure})` : ''}</label>
        <input id="qty-input" aria-label="allocated-quantity" value={allocatedQuantity} onChange={(e) => setAllocatedQuantity(e.target.value)} />
      </section>

      <section aria-label="timeline-section">
        <label htmlFor="start-input">{t('bo.assign.expectedStart')}</label>
        <DateInput id="start-input" aria-label="expected-start" value={expectedStartDate} onValueChange={setExpectedStartDate} />
        <label htmlFor="end-input">{t('bo.assign.expectedCompletion')}</label>
        <DateInput id="end-input" aria-label="expected-completion" value={expectedCompletionDate} onValueChange={setExpectedCompletionDate} />
        <label htmlFor="priority-input">{t('bo.assign.priority')}</label>
        <select id="priority-input" aria-label="priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'EMERGENCY'] as const).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </section>

      {reassigning ? (
        <section aria-label="reassign-section">
          <label htmlFor="reassign-reason">{t('bo.history.reason')}</label>
          <input id="reassign-reason" aria-label="reassign-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <label htmlFor="reassign-remarks">{t('bo.assign.remarks')}</label>
          <input id="reassign-remarks" aria-label="reassign-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </section>
      ) : null}

      <button onClick={assign} aria-label="assign-vendor">{t(reassigning ? 'bo.history.reassign' : 'bo.assign.assign')}</button>
    </main>
  );
}
